import type { LotteryType } from '@/stores/region-store'
import type { JsonLd } from '@/composables/use-seo'

/**
 * 站点名兜底值：仅当后台配置（`/api/v1/site-config` 的 site_name）尚未返回时使用。
 * 与 app.tsx 的全局兜底标题保持一致。真实站点名请通过 useSiteBrand() 动态获取，
 * 不要再写死品牌名（见 GitLab lottery/plans #238）。
 */
export const DEFAULT_SITE_BRAND = '彩票助手'

/**
 * 站点正式访问域名（来自环境变量 VITE_SITE_ORIGIN，结尾 / 已去除）。
 * 未配置时为空字符串，相关绝对地址标签不输出。
 */
export const SITE_ORIGIN = trimTrailingSlashes(import.meta.env.VITE_SITE_ORIGIN ?? '')

/** 去掉结尾的 /。逐字符扫描，代替会平方级回溯的 /\/+$/ */
function trimTrailingSlashes(s: string): string {
  let end = s.length
  while (end > 0 && s[end - 1] === '/') end--
  return s.slice(0, end)
}

/** 彩种中文名映射，用于动态拼接 title/description */
export const LOTTERY_NAME: Record<LotteryType, string> = {
  hk: '香港六合彩',
  macau: '澳门六合彩',
}

/** 取彩种中文名 */
export function lotteryName(type: LotteryType): string {
  return LOTTERY_NAME[type]
}

/** 拼接绝对 URL（基于 SITE_ORIGIN）；未配置域名时返回 undefined */
export function absoluteUrl(path = '/'): string | undefined {
  if (!SITE_ORIGIN) return undefined
  return SITE_ORIGIN + path
}

/** 站点级 JSON-LD：WebSite + Organization（首页 / 全站通用）。brand 取自后台配置的站点名 */
export function siteJsonLd(brand: string): JsonLd {
  const url = absoluteUrl('/') ?? ''
  return {
    '@context': 'https://schema.org',
    '@graph': [
      { '@type': 'WebSite', name: brand, url, inLanguage: 'zh-CN' },
      { '@type': 'Organization', name: brand, url, logo: url ? `${SITE_ORIGIN}/logo.png` : undefined },
    ],
  }
}

/** isPartOf 字段：指回站点 WebSite，复用于各 Dataset。brand 取自后台配置的站点名 */
export function partOfSite(brand: string): JsonLd {
  return { '@type': 'WebSite', name: brand, url: absoluteUrl('/') ?? '' }
}

/** Organization 创建者字段，复用于各 Dataset。brand 取自后台配置的站点名 */
export function creatorOrg(brand: string): JsonLd {
  return { '@type': 'Organization', name: brand }
}

/**
 * 常见问题 JSON-LD（FAQPage）。AI 引擎很爱引用问答对，是加分项。
 * @param items 形如 [{ q: '如何使用？', a: '点击…' }]
 */
export function faqJsonLd(items: { q: string; a: string }[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((it) => ({
      '@type': 'Question',
      name: it.q,
      acceptedAnswer: { '@type': 'Answer', text: it.a },
    })),
  }
}

/**
 * 面包屑 JSON-LD（BreadcrumbList）。
 * @param items 形如 [{ name: '首页', path: '/' }, { name: '澳门六合彩', path: '/...' }]
 */
export function breadcrumbJsonLd(items: { name: string; path?: string }[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      ...(it.path && absoluteUrl(it.path) ? { item: absoluteUrl(it.path) } : {}),
    })),
  }
}
